import { useState, useEffect, useCallback } from 'react';

/**
 * Favorites System - Manage user's favorite tools
 * 
 * Stores favorites in localStorage with tool metadata
 * Provides hooks and utilities for managing favorites across the app
 */

const FAVORITES_STORAGE_KEY = 'coregulate_favorites';
const MAX_FAVORITES = 10;

// Tool metadata for displaying favorites
export const TOOL_METADATA = {
    // Regulation Hub tools
    '/regulate': { 
        title: 'Regulation Tools', 
        description: 'Individual regulating tools for ADHD brains',
        icon: 'Heart', 
        variant: 'sage',
        hub: 'regulation'
    },
    '/regulate/emotion-identifier': { 
        title: 'Making Sense of Your Emotions', 
        description: 'Name what you\'re feeling and find what helps',
        icon: 'Sparkles', 
        variant: 'lavender',
        hub: 'regulation'
    },
    '/regulate/regulation-navigator': { 
        title: 'Regulation Navigator', 
        description: 'Find the right tool for how you feel',
        icon: 'Compass', 
        variant: 'blue',
        hub: 'regulation'
    },
    '/regulate/box-breathing': { 
        title: 'Box Breathing', 
        description: 'Calming breathing exercise',
        icon: 'Wind', 
        variant: 'sage',
        hub: 'regulation'
    },
    '/regulate/micro-reset': { 
        title: 'Micro Reset', 
        description: 'Quick 60-second reset',
        icon: 'Zap', 
        variant: 'lavender',
        hub: 'regulation'
    },
    '/regulate/self-talk': { 
        title: 'Self-Talk Reframes', 
        description: 'Gentle words to shift inner narratives',
        icon: 'MessageCircle', 
        variant: 'rose',
        hub: 'regulation'
    },
    '/regulate/breathing-orb': { 
        title: 'Calm Cycle', 
        description: 'A steady rhythm to settle your system',
        icon: 'Heart', 
        variant: 'lavender',
        hub: 'regulation'
    },
    '/regulate/still-sphere': { 
        title: 'Still Sphere', 
        description: 'A warm, quiet presence for stillness',
        icon: 'Circle', 
        variant: 'gold',
        hub: 'regulation'
    },
    '/regulate/emdr': { 
        title: 'EMDR / Bilateral', 
        description: 'Rhythmic, alternating sensory input to calm & ground',
        icon: 'Eye', 
        variant: 'lavender',
        hub: 'regulation'
    },
    '/regulate/adhd-triggers': { 
        title: 'ADHD Triggers', 
        description: 'Understand your triggers',
        icon: 'Brain', 
        variant: 'lavender',
        hub: 'regulation'
    },
    // Relationships Hub tools
    '/relationship-hub/check-in': { 
        title: 'Relationship Check-In', 
        description: 'Guided assessment to find the right tools',
        icon: 'ClipboardCheck', 
        variant: 'rose',
        hub: 'relationships'
    },
    '/adhd-relationships': { 
        title: 'ADHD & Relationships', 
        description: 'Understanding how ADHD affects your relationships',
        icon: 'Brain', 
        variant: 'lavender',
        hub: 'relationships'
    },
    '/coregulate': { 
        title: 'Co-Regulation', 
        description: 'Regulate together with your partner',
        icon: 'Users', 
        variant: 'rose',
        hub: 'relationships'
    },
    '/communicate': { 
        title: 'Communication Scripts', 
        description: 'Words for difficult conversations',
        icon: 'MessageCircle', 
        variant: 'blue',
        hub: 'relationships'
    },
    '/relationship-hub/repair': { 
        title: 'Repair & Reconnection', 
        description: 'Conversation guides for reconnecting',
        icon: 'RefreshCw', 
        variant: 'rose',
        hub: 'relationships'
    },
    '/relationship-hub/break-the-cycle': { 
        title: 'Break the Cycle', 
        description: 'Interrupt negative patterns',
        icon: 'RefreshCw', 
        variant: 'lavender',
        hub: 'relationships'
    }
};

/**
 * Get all favorites from storage
 */
export const getFavorites = () => {
    try {
        const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error('Error reading favorites:', e);
    }
    return [];
};

/**
 * Check if a tool is favorited
 */
export const isFavorite = (path) => {
    const favorites = getFavorites();
    return favorites.includes(path);
};

/**
 * Add a tool to favorites
 */
export const addFavorite = (path) => {
    const favorites = getFavorites();
    if (!favorites.includes(path) && favorites.length < MAX_FAVORITES) {
        const updated = [path, ...favorites];
        localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(updated));
        return updated;
    }
    return favorites;
};

/**
 * Remove a tool from favorites
 */
export const removeFavorite = (path) => {
    const favorites = getFavorites();
    const updated = favorites.filter(f => f !== path);
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(updated));
    return updated;
};

/**
 * Toggle favorite status
 */
export const toggleFavorite = (path) => {
    if (isFavorite(path)) {
        return { favorites: removeFavorite(path), added: false };
    } else {
        return { favorites: addFavorite(path), added: true };
    }
};

/**
 * Get favorite tools with metadata
 */
export const getFavoritesWithMetadata = () => {
    const favorites = getFavorites();
    return favorites
        .map(path => ({
            path,
            ...TOOL_METADATA[path]
        }))
        .filter(f => f.title); // Only include tools with metadata
};

/**
 * Hook for managing favorites with React state
 */
export const useFavorites = () => {
    const [favorites, setFavorites] = useState([]);

    useEffect(() => {
        setFavorites(getFavorites());
    }, []);

    const toggle = useCallback((path) => {
        const result = toggleFavorite(path);
        setFavorites(result.favorites);
        return result.added;
    }, []);

    const check = useCallback((path) => {
        return favorites.includes(path);
    }, [favorites]);

    const getWithMetadata = useCallback(() => {
        return favorites
            .map(path => ({
                path,
                ...TOOL_METADATA[path]
            }))
            .filter(f => f.title);
    }, [favorites]);

    return {
        favorites,
        toggle,
        isFavorite: check,
        getFavoritesWithMetadata: getWithMetadata,
        count: favorites.length,
        maxFavorites: MAX_FAVORITES
    };
};

export default useFavorites;
